import { extendObservable, runInAction } from 'mobx';

import { AddressController } from '../networking/controllers/AddressController';
import { PasswordConfirmationStore } from './PasswordConfirmationStore';
import { Logger } from '../helpers/Logger';
import { ERROR_STATUSES } from '../models/ApiError';

class AddressStoreClass {
  constructor() {
    extendObservable(this, {
      addresses: null,
      loadingAddresses: false,
      getError: false,
      creationError: false,
      loadingCreation: false,
      loadingEdition: false,
      deletionInternalError: false,
      deletionError: false,
      deletingAddress: false,
      deletionSuccess: false,
    });
  }

  async getUserAddresses() {
    runInAction(() => {
      this.loadingAddresses = true;
    });
    try {
      const addresses = await AddressController.all();
      runInAction(() => {
        this.addresses = addresses;
      });
    } catch (error) {
      Logger.warn(error);
      runInAction(() => {
        this.getError = true;
      });
    } finally {
      runInAction(() => {
        this.loadingAddresses = false;
      });
    }
  }

  async createAddress(address) {
    runInAction(() => {
      this.creationError = false;
      this.loadingCreation = true;
    });
    try {
      const newAddress = await AddressController.create(address);
      runInAction(() => {
        if (!this.addresses) {
          this.addresses = [newAddress];
        } else {
          this.addresses.push(newAddress);
        }
      });
      return newAddress.id;
    } catch (error) {
      Logger.warn(error);
      if (error.response && error.response.status === ERROR_STATUSES.PRECONDITION_FAILED) {
        // Wait for confirmation and try to submit again when done
        try {
          return await PasswordConfirmationStore.showConfirmationModal(() => (
            this.createAddress(address)
          ));
        } catch (creationError) {
          Logger.warn(creationError);
          runInAction(() => {
            this.creationError = creationError;
          });
        }
      }
      runInAction(() => {
        this.creationError = error;
      });
      return null;
    } finally {
      runInAction(() => {
        this.loadingCreation = false;
      });
    }
  }

  async editAddress(addressParams) {
    runInAction(() => {
      this.editionError = false;
      this.loadingEdition = true;
    });
    try {
      const updatedAddress = await AddressController.edit(addressParams);
      runInAction(() => {
        const filteredAddresses = this.addresses.filter(address => address.id !== addressParams.id);
        filteredAddresses.push(updatedAddress);
        this.addresses = filteredAddresses;
      });
      return updatedAddress.id;
    } catch (error) {
      Logger.warn(error);
      if (error.response && error.response.status === ERROR_STATUSES.PRECONDITION_FAILED) {
        // Wait for confirmation and try to submit again when done
        try {
          return await PasswordConfirmationStore.showConfirmationModal(() => (
            this.editAddress(addressParams)
          ));
        } catch (editionError) {
          runInAction(() => {
            this.editionError = editionError;
          });
        }
      }
      runInAction(() => {
        this.editionError = error;
      });
      return null;
    } finally {
      runInAction(() => {
        this.loadingEdition = false;
      });
    }
  }

  async deleteAddress(addressId) {
    runInAction(() => {
      this.deletingAddress = true;
    });
    try {
      await AddressController.delete(addressId);
      runInAction(() => {
        const filteredAddresses = this.addresses.filter(address => address.id !== addressId);
        this.addresses = filteredAddresses;
        this.deletionSuccess = true;
      });
    } catch (error) {
      Logger.warn(error);
      if (error.response && error.response.status === ERROR_STATUSES.PRECONDITION_FAILED) {
        // Wait for confirmation and try to submit again when done
        try {
          await PasswordConfirmationStore.showConfirmationModal(() => (
            this.deleteAddress(addressId)
          ));
        } catch (deletionError) {
          runInAction(() => {
            this.deletionError = deletionError;
          });
        }
      } else if (error.status === ERROR_STATUSES.SERVER_ERROR) {
        runInAction(() => {
          this.deletionInternalError = true;
        });
      } else {
        runInAction(() => {
          this.deletionError = true;
        });
      }
    } finally {
      runInAction(() => {
        this.deletingAddress = false;
      });
    }
  }

  clearCreationAndEditionErrors() {
    runInAction(() => {
      this.creationError = null;
      this.editionError = null;
    });
  }

  clearDeletionErrors() {
    runInAction(() => {
      this.deletionError = false;
      this.deletionInternalError = false;
    });
  }

  clearDeletionSuccess() {
    runInAction(() => {
      this.deletionSuccess = false;
    });
  }

  async getUserAddressesV2() {
    runInAction(() => {
      this.loadingAddresses = true;
    });
    try {
      const addresses = await AddressController.allNewApi();
      runInAction(() => {
        this.addresses = addresses;
      });
    } catch (error) {
      Logger.warn(error);
      runInAction(() => {
        this.getError = true;
      });
    } finally {
      runInAction(() => {
        this.loadingAddresses = false;
      });
    }
  }

  async createAddressV2(address) {
    runInAction(() => {
      this.creationError = false;
      this.loadingCreation = true;
    });
    try {
      const newAddress = await AddressController.createNewApi(address);
      runInAction(() => {
        if (!this.addresses) {
          this.addresses = [newAddress];
        } else {
          this.addresses.push(newAddress);
        }
      });
      return newAddress.id;
    } catch (error) {
      Logger.warn(error);
      runInAction(() => {
        this.creationError = {
          message: error.message,
          content: error.content,
        };
      });
      return null;
    } finally {
      runInAction(() => {
        this.loadingCreation = false;
      });
    }
  }

  async editAddressV2(addressParams) {
    runInAction(() => {
      this.editionError = false;
      this.loadingEdition = true;
    });
    try {
      const updatedAddress = await AddressController.editNewApi(addressParams);
      runInAction(() => {
        const filteredAddresses = this.addresses.filter(address => address.id !== addressParams.id);
        filteredAddresses.push(updatedAddress);
        this.addresses = filteredAddresses;
      });
      return updatedAddress.id;
    } catch (error) {
      Logger.warn(error);
      if (error.response && error.response.status === ERROR_STATUSES.PRECONDITION_FAILED) {
        // Wait for confirmation and try to submit again when done
        try {
          return await PasswordConfirmationStore.showConfirmationModal(() => (
            this.editAddress(addressParams)
          ));
        } catch (editionError) {
          runInAction(() => {
            this.editionError = editionError;
          });
        }
      }
      runInAction(() => {
        this.editionError = error;
      });
      return null;
    } finally {
      runInAction(() => {
        this.loadingEdition = false;
      });
    }
  }
}

const AddressStore = new AddressStoreClass();

export { AddressStore };



// WEBPACK FOOTER //
// ./src/stores/AddressStore.js